import {useState, type ReactNode} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {GroupHeaderFn, ToolGroup, ToolGroupBase} from './types'
import {NewToolHeader} from '../tool/NewToolHeader'
import {AnimatedCollapse} from '../../AnimatedCollapse'
import styles from './ToolGroupRenderer.module.css'
import toolStyles from '../tool/Tool.module.css'
import {computeGroupDuration} from './group-duration'

type ToolGroupRendererProps = {
  group: ToolGroupBase | ToolGroup<unknown>
  groupHeaderFn: GroupHeaderFn
  children: ReactNode
}

function isToolGroup(group: ToolGroupBase | ToolGroup<unknown>): group is ToolGroup<unknown> {
  return 'entries' in group
}

export function ToolGroupRenderer({group, groupHeaderFn, children}: ToolGroupRendererProps) {
  const headerData = groupHeaderFn(group)
  // Groups with 30+ tools start collapsed to avoid overwhelming the UI
  const [isCollapsed, setIsCollapsed] = useState(group.count >= 30)

  const handleToggle = () => {
    setIsCollapsed(prev => !prev)
  }

  const shouldShowDuration = isFeatureEnabled('copilot_mission_control_setup_steps_timing')
  const durationVisual = shouldShowDuration && isToolGroup(group) ? computeGroupDuration(group) : null

  const groupContent = (
    <div className={styles.toolGroupContentWrapper}>
      <div className={styles.connectorLine} />
      <div className={styles.toolGroupContent}>{children}</div>
    </div>
  )

  return (
    <div className={toolStyles.toolContainer}>
      <div className={styles.toolGroupHeader}>
        <NewToolHeader
          title={headerData.title}
          leadingVisual={headerData.leadingVisual}
          trailingVisual={headerData.trailingVisual}
          titleSuffix={durationVisual}
          isError={false}
          isPending={false}
          isCollapsed={isCollapsed}
          isGroupHeader
          onToggle={handleToggle}
        />
      </div>
      <AnimatedCollapse open={!isCollapsed}>{groupContent}</AnimatedCollapse>
    </div>
  )
}
